import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { BRANDS } from "@/data/content";

const BrandRow = ({ brand, index, lang, t }) => {
  const reverse = index % 2 === 1;

  const badges = (() => {
    const list = [];
    if (brand.cardBadge) list.push(brand.cardBadge[lang]);
    else if (brand.status === "closed") list.push(t.brands.closedLabel);
    else if (brand.status === "coming_soon") list.push(t.brands.comingSoonLabel);
    if (brand.isNew) list.push(t.brands.newLabel);
    return list;
  })();

  return (
    <Reveal delay={0.05}>
      <Link
        to={`/brands/${brand.slug}`}
        data-testid={`brand-${brand.key}`}
        className="grid grid-cols-1 lg:grid-cols-2 overflow-hidden group cursor-pointer"
      >
        {/* Logo panel */}
        <div
          className={`relative min-h-[280px] md:min-h-[420px] flex items-center justify-center p-10 ${
            reverse ? "lg:order-2" : ""
          }`}
          style={{ backgroundColor: brand.bg }}
        >
          {badges.length > 0 && (
            <div className="absolute top-6 start-6 flex flex-wrap gap-2">
              {badges.map((text, i) => (
                <span
                  key={i}
                  className="text-xs uppercase tracking-[0.2em] px-3 py-1 border backdrop-blur-sm"
                  style={{
                    color: brand.accent,
                    borderColor: brand.accent,
                    backgroundColor: brand.status === "closed" ? "rgba(0,0,0,0.35)" : "transparent",
                  }}
                >
                  {text}
                </span>
              ))}
            </div>
          )}
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.name[lang]}
              className={`max-h-40 md:max-h-56 w-auto object-contain transition-all duration-700 group-hover:scale-105 ${
                brand.status === "closed" ? "opacity-60" : ""
              }`}
            />
          ) : (
            <div className="text-center">
              <div className="font-display text-5xl md:text-7xl mb-2" style={{ color: brand.accent }}>
                {brand.name.en}
              </div>
              <div className="text-2xl md:text-3xl font-display" style={{ color: "#F5F2EB" }}>
                {brand.name.ar}
              </div>
            </div>
          )}
        </div>

        {/* Content panel */}
        <div
          className={`relative min-h-[280px] md:min-h-[420px] flex flex-col justify-center p-10 md:p-16 bg-white ${
            reverse ? "lg:order-1" : ""
          }`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={brand.image}
              alt=""
              className="w-full h-full object-cover opacity-0 group-hover:opacity-15 scale-110 group-hover:scale-100 transition-all duration-1000"
            />
          </div>
          <div className="relative">
            <span className="text-xs uppercase tracking-[0.24em] text-[#6A1E27]">
              {brand.subtitle?.[lang] || brand.tag[lang]}
            </span>
            <h3 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mt-3 mb-2">
              {brand.name[lang]}
            </h3>
            {lang === "en" && (
              <p className="font-display text-lg text-[#8A8478] mb-5">{brand.name.ar}</p>
            )}
            <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-md">
              {brand.desc[lang]}
            </p>
            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-block w-12 h-1" style={{ backgroundColor: brand.bg }} />
                <span className="text-sm text-[#8A8478]">
                  0{index + 1} / 0{BRANDS.length}
                </span>
              </div>
              <span className="inline-flex items-center gap-2 text-sm text-[#6A1E27] opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-500">
                {t.brands.visit}
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

const Brands = () => {
  const { t, lang, dir } = useLang();

  return (
    <section id="brands" data-testid="brands-section" className="py-24 md:py-32 bg-[#F5F2EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <Reveal>
            <Kicker>{t.brands.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              style={{ lineHeight: dir === "rtl" ? 1.25 : 1.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] mt-5"
            >
              {t.brands.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg text-[#4A4A4A] mt-4">{t.brands.subtitle}</p>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
        {BRANDS.map((b, i) => (
          <BrandRow key={b.key} brand={b} index={i} lang={lang} t={t} />
        ))}
      </div>
    </section>
  );
};

export default Brands;
